import React, { useState, useEffect } from 'react';
import { PrimaryButton, SecondaryButton, DangerButton } from '@/components/Buttons';
import ModalBasic from '@/components/ModalBasic';
import { 
  getCloverCategories, 
  getCategoryMappings, 
  createCategoryMapping, 
  updateCategoryMapping,
} from '@/api/externalFeeds';
import { getCustomCategories } from '@/api/inventory';
import { addToast } from '@/store/app/action';
import { useDispatch } from 'react-redux';
import Loader from '@/components/Loader';

const CategoriesMapping = () => {
  const dispatch = useDispatch();
  const [cloverCategories, setCloverCategories] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [updatingMappingId, setUpdatingMappingId] = useState(null);

  // Fetch Clover categories, custom categories, and mappings on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [categoriesRes, mappingsRes, customCategoriesRes] = await Promise.all([
          getCloverCategories(),
          getCategoryMappings(),
          getCustomCategories()
        ]);

        if (categoriesRes.success) {
          setCloverCategories(categoriesRes.categories || []);
        }

        if (mappingsRes.success) {
          setMappings(mappingsRes.mappings || []);
        }

        if (customCategoriesRes && customCategoriesRes.categories) {
          setCustomCategories(customCategoriesRes.categories || []);
        }
      } catch (error) {
        console.error('Failed to fetch categories/mappings:', error);
        dispatch(addToast({
          type: 'error',
          message: 'Failed to load category data',
          timer: 5
        }));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  const handleInternalCategoryChange = async (mappingId, newInternalCategory) => {
    setUpdatingMappingId(mappingId);
    try {
      const response = await updateCategoryMapping(mappingId, newInternalCategory, false);
      if (response.success) {
        setMappings(mappings.map(mapping => {
          const id = getMappingId(mapping);
          return id === mappingId
            ? { ...mapping, internal_category: newInternalCategory, ignore: false }
            : mapping;
        }));
        dispatch(addToast({
          type: 'success',
          message: 'Category mapping updated successfully',
          timer: 5
        }));
      }
    } catch (error) {
      console.error('Failed to update mapping:', error);
      dispatch(addToast({
        type: 'error',
        message: error.response?.data?.error || 'Failed to update category mapping',
        timer: 5
      }));
    } finally {
      setUpdatingMappingId(null);
    }
  };

  const handleDeleteClick = (mappingId) => {
    setMappingToDelete(mappingId);
    setShowDeleteModal(true);
  };

  const handleDeleteMapping = async () => {
    if (!mappingToDelete) return;

    try {
      // Instead of deleting the mapping, mark it as ignored and clear internal_category
      const response = await updateCategoryMapping(mappingToDelete, '', true);
      if (response.success) {
        setMappings(mappings.map(mapping => {
          const id = getMappingId(mapping);
          if (id !== mappingToDelete) return mapping;
          return {
            ...mapping,
            internal_category: null,
            ignore: true,
          };
        }));
        setShowDeleteModal(false);
        setMappingToDelete(null);
        dispatch(addToast({
          type: 'success',
          message: 'Category will be ignored for future imports',
          timer: 5
        }));
      }
    } catch (error) {
      console.error('Failed to delete mapping:', error);
      dispatch(addToast({
        type: 'error',
            message: error.response?.data?.error || 'Failed to ignore category mapping',
        timer: 5
      }));
    }
  };

  // Helper to get mapping ID (handle both string and object ID formats)
  const getMappingId = (mapping) => {
    return typeof mapping.id === 'string' ? mapping.id : mapping.id?.$oid || mapping._id || mapping.id;
  };

  // Helper to get mapping for a given POS category name
  const getMappingForCategory = (categoryName) => {
    return mappings.find((m) => m.external_category === categoryName);
  };

  // Helper to get category name by ID
  const getCategoryNameById = (categoryId) => {
    if (!categoryId) return null;
    // Handle both string and ObjectID formats
    const idStr = typeof categoryId === 'string' ? categoryId : categoryId?.$oid || categoryId?.toString();
    const category = customCategories.find(c => {
      const catId = c._id?.$oid || c._id || c.id;
      return catId === idStr || catId?.toString() === idStr;
    });
    return category?.name || null;
  };

  // Helper to normalize category ID for comparison
  const normalizeCategoryId = (id) => {
    if (!id) return null;
    if (typeof id === 'string') return id;
    return id?.$oid || id?.toString() || id;
  };

  // Handle inline Saucey category change (create or update mapping)
  const handleSauceyCategoryChange = async (categoryName, mapping, newInternalCategory) => {
    if (!newInternalCategory) {
      return;
    }

    // Update existing mapping
    if (mapping) {
      const mappingId = getMappingId(mapping);
      return handleInternalCategoryChange(mappingId, newInternalCategory);
    }

    // Create new mapping
    try {
      const response = await createCategoryMapping(categoryName, newInternalCategory);
      if (response.success) {
        setMappings([...mappings, response.mapping]);
        dispatch(addToast({
          type: 'success',
          message: 'Category mapping created successfully',
          timer: 5
        }));
      }
    } catch (error) {
      console.error('Failed to create mapping:', error);
      dispatch(addToast({
        type: 'error',
        message: error.response?.data?.error || 'Failed to create category mapping',
        timer: 5
      }));
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader />
          <span className="ml-2 text-slate-600 dark:text-slate-300">Loading categories...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <p className="text-slate-600 dark:text-slate-300">
              Map POS category names from your POS system to Saucey category names used in your platform.
            </p>
          </div>
          
          {cloverCategories.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <p>No categories found. Please run a sync to fetch categories from your POS system.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table-auto w-full">
                <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20">
                  <tr>
                    <th className="px-4 py-3 text-left">POS Category</th>
                    <th className="px-4 py-3 text-left">Saucey Category</th>
                    <th className="px-4 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                  {cloverCategories.map((category) => {
                    const mapping = getMappingForCategory(category.name);
                    const mappingId = mapping ? getMappingId(mapping) : null;
                    return (
                      <tr key={category.id || category._id || category.name} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="px-4 py-3 text-slate-800 dark:text-slate-100">
                          {category.name}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={mapping && !mapping.ignore ? normalizeCategoryId(mapping.internal_category) || '' : ''}
                            onChange={(e) => handleSauceyCategoryChange(category.name, mapping, e.target.value)}
                            disabled={mapping && updatingMappingId === mappingId}
                            className="text-sm border border-slate-200 dark:border-slate-700 rounded px-2 py-1 bg-white dark:bg-theme-surface-accent text-slate-800 dark:text-slate-100 w-full disabled:opacity-50"
                          >
                            <option value="">Not Mapped</option>
                            {customCategories.map((customCategory) => {
                              const catId = customCategory._id?.$oid || customCategory._id || customCategory.id;
                              return (
                                <option key={catId} value={catId}>
                                  {customCategory.name}
                                </option>
                              );
                            })}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          {mapping && (
                            <button
                              onClick={() => handleDeleteClick(mappingId)}
                              className="text-red-400 hover:text-red-500 dark:text-red-500 dark:hover:text-red-400"
                              title="Delete Mapping"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ModalBasic
        id="delete-category-mapping-modal"
        title="Ignore Category"
        modalOpen={showDeleteModal}
        setModalOpen={setShowDeleteModal}
        width="sm"
      >
        <div className="space-y-4">
          <p className="text-slate-600 dark:text-slate-400">
            Are you sure you want to ignore this POS category? It will be skipped during imports, but you can remap it later.
          </p>
          <div className="flex gap-2 justify-end">
            <SecondaryButton onClick={() => {
              setShowDeleteModal(false);
              setMappingToDelete(null);
            }}>
              Cancel
            </SecondaryButton>
            <DangerButton onClick={handleDeleteMapping}>
              Ignore Category
            </DangerButton>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
};

export default CategoriesMapping; 